import { Directive, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import * as lodash from 'lodash';
import { merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { convertQueryParamMap } from 'app/shared/util/pagination-util';



@Directive()
export abstract class ListWithFiltersBase<T> implements OnInit, OnDestroy {
  @Output() readonly queryChange = new EventEmitter<Params>();

  abstract filters: FormGroup;
  abstract filterTypes: Record<string, App.FilterParamType>;
  filtersApplied: boolean = false;
  @ViewChild('paginator', { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sortor: MatSort;
  queryParams: Params;

  dataSource: Array<T> = [];
  total: number = 0;
  loading: boolean = false;
  loaded: boolean = false;

  protected subscription: Subscription = new Subscription();

  constructor(protected router: Router, protected route: ActivatedRoute) {
  }

  ngOnInit() {
    this.subscription.add(
      this.filters.valueChanges
        .pipe(
          distinctUntilChanged(lodash.isEqual)
        )
        .subscribe((values): void => {
          this.filtersApplied = !lodash.isEmpty(lodash.pickBy(values));
        })
    );

    this.subscription.add(
      merge(this.filters.valueChanges, this.sortor.sortChange).subscribe((): void => {
        this.paginator.pageIndex = 0;
      })
    );

    this.subscription.add(
      merge(this.filters.valueChanges, this.paginator.page, this.sortor.sortChange)
        .pipe(
          debounceTime(300)
        )
        .subscribe((): void => {
          this.queryParams = lodash.pickBy(lodash.merge({
              page: this.paginator.pageIndex,
              size: this.paginator.pageSize
            }, {
              sort: this.sortor.active,
              dir: this.sortor.direction
            }, this.filters.value));
          this.queryChange.emit(this.queryParams);
          this.router.navigate([], {
            replaceUrl: true,
            queryParams: this.queryParams
          });
        })
    );

    this.initFilters();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private initFilters(): void {
    const queryParamValues: Record<string, unknown> & {
      page?: number;
      size?: number;
      sort?: string;
      dir?: SortDirection;
    } = convertQueryParamMap(this.route.snapshot.queryParamMap, this.filterTypes);
    const filterValues = lodash.clone(queryParamValues);

    if (lodash.isNumber(queryParamValues.page)) {
      this.paginator.pageIndex = queryParamValues.page;
    }
    if (lodash.isNumber(queryParamValues.size)) {
      this.paginator.pageSize = queryParamValues.size;
    }
    if (lodash.isString(queryParamValues.sort)) {
      this.sortor.active = queryParamValues.sort;
    }
    if (lodash.isString(queryParamValues.dir)) {
      this.sortor.direction = queryParamValues.dir;
    }
    delete filterValues.page;
    delete filterValues.size;
    delete filterValues.sort;
    delete filterValues.dir;
    this.filters.setValue(filterValues);
  }
}
